/**
 * AssistantInboxPanel — Mobiler Posteingang der Agenten-Vorschläge.
 *
 * Zeigt die offenen Vorschläge deiner Fach-Agenten. Triage: Erledigt / Ablehnen.
 * SICHERHEIT: Login-Pflicht (Backend). Triage ist nicht-destruktiv + protokolliert.
 */

import { useEffect, useState } from 'react';
import { X, Inbox, Loader2, Check, Ban } from 'lucide-react';
import { supabase } from '@/lib/supabase';

async function apiPost(body: any) {
  const { data: sess } = await supabase.auth.getSession();
  const token = sess?.session?.access_token;
  const res = await fetch('/api/assistant-vorschlaege', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token || ''}` },
    body: JSON.stringify(body),
  });
  return res.json().catch(() => ({}));
}

type V = { id: string; agent_id?: string; agent_name?: string; kategorie?: string; titel?: string; beschreibung?: string; vorschlag_text?: string; prioritaet?: string; erstellt_am?: string };

export default function AssistantInboxPanel({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<V[]>([]);
  const [gesamt, setGesamt] = useState(0);
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    apiPost({ action: 'list' }).then((d) => {
      setItems(Array.isArray(d?.vorschlaege) ? d.vorschlaege : []);
      setGesamt(typeof d?.gesamt === 'number' ? d.gesamt : 0);
      setLoading(false);
    });
  }, [open]);

  async function triage(id: string, entscheidung: 'erledigt' | 'abgelehnt') {
    setBusyId(id);
    const d = await apiPost({ action: 'triage', id, entscheidung });
    if (d?.ok) {
      setItems((xs) => xs.filter((x) => x.id !== id));
      setGesamt((g) => Math.max(0, g - 1));
    }
    setBusyId(null);
  }

  if (!open) return null;

  return (
    <div className="md:hidden fixed inset-0 z-[110] flex flex-col bg-gradient-to-b from-[#0e1430] via-[#0a0f22] to-[#070a16] text-slate-100">
      <div className="flex items-center gap-3 px-4 pt-[calc(env(safe-area-inset-top)+14px)] pb-3 border-b border-white/10">
        <Inbox className="w-6 h-6 text-[#6d8bff]" />
        <div className="flex-1">
          <p className="text-sm font-semibold">Posteingang</p>
          <p className="text-[11px] text-slate-400">{gesamt} offene Agenten-Vorschläge</p>
        </div>
        <button onClick={onClose} aria-label="Schließen" className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-slate-300">
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-2.5">
        {loading && <div className="text-slate-400 text-sm flex items-center gap-2 px-1"><Loader2 className="w-4 h-4 animate-spin" /> Lädt …</div>}
        {!loading && items.length === 0 && <div className="text-slate-400 text-sm px-1 py-8 text-center">Nichts offen — sauber! 🎉</div>}

        {items.map((v) => (
          <div key={v.id} className="rounded-2xl border border-white/10 bg-white/[0.04] p-3">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-[#6d8bff]/20 text-[#a9bcff]">{v.agent_name || v.agent_id || 'Agent'}</span>
              {v.prioritaet && <span className="text-[10px] text-amber-300">{v.prioritaet}</span>}
              {v.kategorie && <span className="text-[10px] text-slate-500">· {v.kategorie}</span>}
            </div>
            <p className="text-[13.5px] font-medium text-slate-100">{v.titel || '(ohne Titel)'}</p>
            {(v.beschreibung || v.vorschlag_text) && (
              <p className="text-[12px] text-slate-400 mt-1 line-clamp-4 whitespace-pre-wrap">{(v.beschreibung || v.vorschlag_text || '').slice(0, 400)}</p>
            )}
            <div className="flex gap-2 mt-2.5">
              <button disabled={busyId === v.id} onClick={() => triage(v.id, 'erledigt')} className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-emerald-600/90 text-white text-[12px] disabled:opacity-50">
                <Check className="w-4 h-4" /> Erledigt
              </button>
              <button disabled={busyId === v.id} onClick={() => triage(v.id, 'abgelehnt')} className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg border border-white/15 text-slate-300 text-[12px] disabled:opacity-50">
                <Ban className="w-4 h-4" /> Ablehnen
              </button>
            </div>
          </div>
        ))}
        <p className="text-[10px] text-slate-500 px-1 pt-1">Triage ändert nur den Status eines Vorschlags — nichts wird gelöscht, alles wird protokolliert.</p>
      </div>
    </div>
  );
}
